import { useLayoutEffect, useRef } from 'react'
import JsBarcode from 'jsbarcode'

/** Blank margin Code 128 needs on each side, in modules (ISO/IEC 15417). */
const QUIET_ZONE_MODULES = 10

/**
 * CODE128 lets JsBarcode switch between subsets A/B/C on its own — digit runs
 * go in subset C, two digits per symbol, which keeps the bars wide.
 */
const FORMAT = 'CODE128'

interface Props {
  value: string
  height?: number
  moduleWidth?: number
  color?: string
  /**
   * Print the barcode at its real size instead of stretching it to the
   * container: every module is exactly this many millimetres. Pick a whole
   * number of printer dots — a fractional one is rounded differently bar by bar
   * and the scanner misreads the widths.
   */
  moduleWidthMm?: number
  /** Bar height in millimetres; only with `moduleWidthMm`. */
  heightMm?: number
}

/** Width of the encoded value in modules, quiet zones included. */
export function getBarcodeModules(value: string) {
  const data: { encodings?: { data: string }[] } = {}
  try {
    JsBarcode(data, value, { format: FORMAT })
  } catch {
    return 0
  }
  // Each character of `data` is one module ('1' bar, '0' space).
  const modules = (data.encodings ?? []).reduce(
    (sum, encoding) => sum + encoding.data.length,
    0,
  )
  return modules + QUIET_ZONE_MODULES * 2
}

/**
 * Label printers here (HPRT HT300) print at 203 dpi — 8 dots per millimetre.
 * A module must be a whole number of dots, otherwise the driver rounds bars
 * unevenly and the scanner misreads them.
 */
export const LABEL_PRINTER_DOTS_PER_MM = 8

/**
 * The widest module — a whole number of printer dots, at most `maxDots` — at
 * which `value` still fits `maxWidthMm`, quiet zones included. `undefined`
 * when even one dot per module is too wide; the barcode then stretches.
 */
export function fitModuleWidthMm(
  value: string,
  maxWidthMm: number,
  { dotsPerMm = LABEL_PRINTER_DOTS_PER_MM, maxDots = 3 } = {},
): number | undefined {
  const modules = getBarcodeModules(value)
  if (!modules) return undefined
  for (let dots = maxDots; dots >= 1; dots--) {
    const width = dots / dotsPerMm
    if (modules * width <= maxWidthMm) return width
  }
  return undefined
}

/**
 * Renders a CODE128 barcode (JsBarcode) into an inline SVG, so it survives
 * react-to-print (which serializes the DOM) without extra config.
 */
export function Barcode({
  value,
  height = 70,
  moduleWidth = 2,
  color = '#000',
  moduleWidthMm,
  heightMm = 8,
}: Props) {
  const ref = useRef<SVGSVGElement>(null)

  useLayoutEffect(() => {
    const svg = ref.current
    if (!svg) return

    // Drawn in module units when printed at real size; the SVG's own size
    // then decides how big a module is.
    const unit = moduleWidthMm ? 1 : moduleWidth
    const barHeight = moduleWidthMm ? heightMm / moduleWidthMm : height

    try {
      JsBarcode(svg, value, {
        format: FORMAT,
        width: unit,
        height: barHeight,
        margin: 0,
        marginLeft: QUIET_ZONE_MODULES * unit,
        marginRight: QUIET_ZONE_MODULES * unit,
        displayValue: false,
        lineColor: color,
        background: 'transparent',
      })
    } catch {
      // Not encodable (e.g. outside ASCII) — leave the space empty.
      svg.replaceChildren()
      return
    }

    // JsBarcode sizes the SVG in px ("210px") with a matching viewBox; keep the
    // viewBox and only replace the outer size.
    const drawnWidth = parseFloat(svg.getAttribute('width') ?? '') || 0
    if (moduleWidthMm) {
      svg.setAttribute('width', `${(drawnWidth / unit) * moduleWidthMm}mm`)
      svg.setAttribute('height', `${heightMm}mm`)
      svg.removeAttribute('preserveAspectRatio')
    } else {
      svg.setAttribute('width', '100%')
      svg.setAttribute('height', String(height))
      svg.setAttribute('preserveAspectRatio', 'none')
    }
  }, [value, height, moduleWidth, color, moduleWidthMm, heightMm])

  return (
    <svg
      ref={ref}
      shapeRendering="crispEdges"
      role="img"
      aria-label={value}
    />
  )
}
